import type { Editor, JSONContent } from '@tiptap/react';
import type {
  ClawSkillSource,
  ClawTemplateToken,
  ClawTemplateTokenFile,
  ClawUploadAcceptKind,
  TextPromptMentionMediaType,
  TextPromptWithInputItem,
} from '@/app/types';

const escapeHTMLAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const getStringAttr = (value: unknown) => (typeof value === 'string' ? value : '');

const toSkillSource = (value: unknown): ClawSkillSource | undefined =>
  value === 'installed' || value === 'platform' ? value : undefined;

const getClawTemplateTokenOptions = (value: unknown): ClawTemplateToken['options'] => {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return undefined;
    return parsed.flatMap((item) => {
      if (
        typeof item === 'object' &&
        item !== null &&
        typeof item.label === 'string' &&
        typeof item.value === 'string'
      ) {
        return [{ label: item.label, value: item.value }];
      }
      return [];
    });
  } catch {
    return undefined;
  }
};

const getClawTemplateTokenFiles = (value: unknown): ClawTemplateTokenFile[] | undefined => {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return undefined;
    return parsed.flatMap((item) => {
      if (
        typeof item === 'object' &&
        item !== null &&
        typeof item.filePath === 'string' &&
        typeof item.fileName === 'string'
      ) {
        return [
          {
            filePath: item.filePath,
            fileName: item.fileName,
            fileType: toMentionMediaType(item.fileType),
            previewUrl: typeof item.previewUrl === 'string' ? item.previewUrl : undefined,
          },
        ];
      }
      return [];
    });
  } catch {
    return undefined;
  }
};

const UPLOAD_ACCEPT_KINDS = new Set<ClawUploadAcceptKind>(['image', 'video', 'audio', 'file']);

const getClawTemplateTokenAccept = (value: unknown): ClawUploadAcceptKind[] | undefined => {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return undefined;
    const accept = parsed.filter((item): item is ClawUploadAcceptKind =>
      UPLOAD_ACCEPT_KINDS.has(item as ClawUploadAcceptKind),
    );
    return accept.length ? Array.from(new Set(accept)) : undefined;
  } catch {
    return undefined;
  }
};

export const getClawTemplateTokenText = (token: ClawTemplateToken): string => {
  if (token.kind === 'skill') {
    return `/${token.label}`;
  }
  if (token.kind === 'upload' && token.files?.length) {
    return token.files.map((file) => `@${file.filePath || file.fileName}`).join(' ');
  }
  if (token.kind === 'input' || token.kind === 'select') {
    return token.value?.trim() ?? '';
  }
  if (token.kind === 'upload') {
    return token.value?.trim() ?? '';
  }
  return token.label;
};

const toMentionMediaType = (value: unknown): TextPromptMentionMediaType | undefined => {
  if (value === 'image' || value === 'video' || value === 'audio' || value === 'file') {
    return value;
  }
  return undefined;
};

/**
 * 计算 TextPromptWithInputItem 的字符长度（用于字数限制等）
 * - text: 按文本长度
 * - mention: 按 @ + name 长度
 * - claw-template-token: 按可发送文本长度
 * - agent-model: 按 description 长度
 */
export const getTextPromptLength = (content: TextPromptWithInputItem): number => {
  let contentLength = 0;
  content.forEach((item) => {
    if (item.type === 'text') {
      contentLength += item.text.length;
    } else if (item.type === 'mention') {
      contentLength += item.mention.name.length + 1;
    } else if (item.type === 'claw-template-token') {
      contentLength += getClawTemplateTokenText(item.clawTemplateToken).length;
    } else if (item.type === 'agent-model') {
      contentLength += item.agentModel.description.length;
    }
  });
  return contentLength;
};

export const textPromptToHTML = (textPrompt: TextPromptWithInputItem) => {
  if (textPrompt.length === 0) return '';

  const paragraphs: string[] = [''];
  let currentParagraphIndex = 0;

  textPrompt.forEach((item) => {
    switch (item.type) {
      case 'text': {
        const segments = item.text.split('\n');

        // 将第一个文本段添加到当前段落
        paragraphs[currentParagraphIndex] += segments[0];

        // 如果有多个段落，创建新段落
        for (let i = 1; i < segments.length; i++) {
          currentParagraphIndex++;
          paragraphs[currentParagraphIndex] = segments[i];
        }
        break;
      }
      case 'mention':
        // 将mention添加到当前段落
        paragraphs[currentParagraphIndex] +=
          `<span data-type="mention" data-id="${escapeHTMLAttribute(item.mention.id)}" data-label="${escapeHTMLAttribute(item.mention.name)}" data-color="${escapeHTMLAttribute(item.mention.color)}"${item.mention.preview ? ` data-preview="${escapeHTMLAttribute(item.mention.preview)}"` : ''}${item.mention.mediaType ? ` data-media-type="${escapeHTMLAttribute(item.mention.mediaType)}"` : ''}>@${item.mention.name}</span>`;
        break;
      case 'claw-template-token': {
        const { clawTemplateToken } = item;
        const options = clawTemplateToken.options?.length
          ? ` data-options="${escapeHTMLAttribute(JSON.stringify(clawTemplateToken.options))}"`
          : '';
        const files = clawTemplateToken.files?.length
          ? ` data-files="${escapeHTMLAttribute(JSON.stringify(clawTemplateToken.files))}"`
          : '';
        const accept = clawTemplateToken.accept?.length
          ? ` data-accept="${escapeHTMLAttribute(JSON.stringify(clawTemplateToken.accept))}"`
          : '';
        const source = clawTemplateToken.source
          ? ` data-source="${escapeHTMLAttribute(clawTemplateToken.source)}"`
          : '';
        const nodeText =
          clawTemplateToken.kind === 'skill'
            ? `/${clawTemplateToken.label}`
            : clawTemplateToken.value || clawTemplateToken.label;
        paragraphs[currentParagraphIndex] +=
          `<span data-type="claw-template-token" data-id="${escapeHTMLAttribute(clawTemplateToken.id)}" data-kind="${escapeHTMLAttribute(clawTemplateToken.kind)}" data-label="${escapeHTMLAttribute(clawTemplateToken.label)}"${clawTemplateToken.value ? ` data-value="${escapeHTMLAttribute(clawTemplateToken.value)}"` : ''}${clawTemplateToken.placeholder ? ` data-placeholder="${escapeHTMLAttribute(clawTemplateToken.placeholder)}"` : ''}${options}${files}${accept}${source}>${escapeHTMLAttribute(nodeText)}</span>`;
        break;
      }
      case 'agent-model': {
        paragraphs[currentParagraphIndex] +=
          `<span data-type="agent-model" data-id="${item.agentModel.id}" data-label="${item.agentModel.name}" data-description="${item.agentModel.description}" data-original-description="${item.agentModel.originalDescription}"><img src="${item.agentModel.avatar}" alt="${item.agentModel.name}" />${item.agentModel.description}</span>`;
        break;
      }
    }
  });

  return paragraphs.map((p) => `<p>${p}</p>`).join('');
};

export const extractMentionsIdsFromEditor = (editor: Editor) => {
  const mentionsIds: string[] = [];
  if (!editor) return mentionsIds;

  editor.state.doc.descendants((node: Editor['state']['doc']) => {
    if (node.type.name === 'mention') {
      mentionsIds.push(node.attrs.id);
    } else if (node.type.name === 'claw-template-token' && node.attrs.kind === 'upload') {
      const files = getClawTemplateTokenFiles(node.attrs.files);
      files?.forEach((file) => {
        if (file.filePath) {
          mentionsIds.push(file.filePath);
        }
      });
    }
  });

  return mentionsIds;
};

export const editorJsonToTextPrompt = (json: JSONContent): TextPromptWithInputItem => {
  const content: TextPromptWithInputItem = [];
  let currentTextChunk = '';

  const processParagraphs = (paragraphs: JSONContent[]) => {
    paragraphs.forEach((paragraph, pIndex) => {
      if (paragraph.type === 'paragraph') {
        // 检查是否为空段落（可能只有br标签）
        const isEmpty = !paragraph.content || paragraph.content.length === 0;

        // 处理段落内容
        if (!isEmpty && paragraph.content) {
          paragraph.content.forEach((node) => {
            switch (node.type) {
              case 'text':
                currentTextChunk += node.text || '';
                break;
              case 'mention':
                if (currentTextChunk) {
                  content.push({ type: 'text', text: currentTextChunk });
                  currentTextChunk = '';
                }

                content.push({
                  type: 'mention',
                  mention: {
                    id: getStringAttr(node.attrs?.id),
                    name: getStringAttr(node.attrs?.label),
                    color: getStringAttr(node.attrs?.color),
                    preview: getStringAttr(node.attrs?.preview) || undefined,
                    mediaType: toMentionMediaType(node.attrs?.mediaType),
                  },
                });
                break;
              case 'claw-template-token':
                if (currentTextChunk) {
                  content.push({ type: 'text', text: currentTextChunk });
                  currentTextChunk = '';
                }
                content.push({
                  type: 'claw-template-token',
                  clawTemplateToken: {
                    id: getStringAttr(node.attrs?.id),
                    kind:
                      node.attrs?.kind === 'skill' ||
                      node.attrs?.kind === 'upload' ||
                      node.attrs?.kind === 'input' ||
                      node.attrs?.kind === 'select'
                        ? node.attrs.kind
                        : 'input',
                    label: getStringAttr(node.attrs?.label),
                    value: getStringAttr(node.attrs?.value) || undefined,
                    placeholder: getStringAttr(node.attrs?.placeholder) || undefined,
                    options: getClawTemplateTokenOptions(node.attrs?.options),
                    files: getClawTemplateTokenFiles(node.attrs?.files),
                    accept: getClawTemplateTokenAccept(node.attrs?.accept),
                    source: toSkillSource(node.attrs?.source),
                  },
                });
                break;
              case 'agent-model':
                if (currentTextChunk) {
                  content.push({ type: 'text', text: currentTextChunk });
                  currentTextChunk = '';
                }
                content.push({
                  type: 'agent-model',
                  agentModel: {
                    id: node.attrs?.id || '',
                    name: node.attrs?.name || '',
                    avatar: node.attrs?.avatar || '',
                    description: node.attrs?.description || '',
                    originalDescription: node.attrs?.originalDescription || '',
                  },
                });
                break;
              default:
                break;
            }
          });
        }

        // 每个段落结束后添加换行符（除了最后一个段落）
        if (pIndex < paragraphs.length - 1) {
          currentTextChunk += '\n';
        }
      }
    });
  };

  processParagraphs(json.content || []);

  if (currentTextChunk) {
    content.push({ type: 'text', text: currentTextChunk });
  }

  return content;
};

export const truncateContentToMaxLength = (
  content: TextPromptWithInputItem,
  maxLength: number,
): TextPromptWithInputItem => {
  const truncatedContent: TextPromptWithInputItem = [];
  let currentLength = 0;

  for (const item of content) {
    // 计算当前项的长度
    const itemLength =
      item.type === 'text'
        ? item.text.length
        : item.type === 'mention'
          ? item.mention.name.length + 1 // +1 for '@'
          : item.type === 'claw-template-token'
            ? getClawTemplateTokenText(item.clawTemplateToken).length
            : item.type === 'agent-model'
              ? item.agentModel.description.length
              : 0;

    // 检查添加这一项是否会超出最大长度
    if (currentLength + itemLength <= maxLength) {
      // 完整添加这一项
      truncatedContent.push(item);
      currentLength += itemLength;
    } else if (item.type === 'text') {
      // 对于文本，可以部分添加
      const remainingSpace = maxLength - currentLength;
      if (remainingSpace > 0) {
        truncatedContent.push({
          type: 'text',
          text: item.text.substring(0, remainingSpace),
        });
      }
      // 截断到此为止
      break;
    } else {
      // 对于mention，如果无法完整添加，则不添加
      break;
    }
  }

  return truncatedContent;
};
